import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { Company } from '../../types/intellijetnet'
import { ensureArray, executeQuery, executeQueryFirst } from './utils'

export class CompaniesService {
  private static readonly TABLE_NAME = 'companies'

  /**
   * Get query builder for companies
   */
  static query(): QueryBuilder {
    return intelliJetNetService.from(this.TABLE_NAME)
  }

  /**
   * Get company by ID
   */
  static async getById(companyId: number): Promise<Company | null> {
    return executeQueryFirst<Company>(
      this.query().eq('company_id', companyId)
    )
  }

  /**
   * Search companies by name
   */
  static async searchByName(searchTerm: string): Promise<Company[]> {
    return executeQuery<Company>(
      this.query()
        .ilike('company_name', `%${searchTerm}%`)
        .eq('is_active', true)
        .order('company_name')
    )
  }

  /**
   * Get companies by business type
   */
  static async getByBusinessType(businessType: string): Promise<Company[]> {
    return executeQuery<Company>(
      this.query()
        .eq('business_type', businessType)
        .eq('is_active', true)
        .order('company_name')
    )
  }

  /**
   * Get companies by location
   */
  static async getByLocation(params: {
    country?: string
    state?: string
    city?: string
  }): Promise<Company[]> {
    let query = this.query().eq('is_active', true)

    if (params.country) query = query.eq('country', params.country)
    if (params.state) query = query.eq('state', params.state)
    if (params.city) query = query.eq('city', params.city)

    return executeQuery<Company>(
      query.order('company_name')
    )
  }

  /**
   * Get companies that own specific aircraft
   */
  static async getByAircraftId(aircraftId: number): Promise<Company[]> {
    // Use cs (contains) operator with proper format for array contains single value
    return executeQuery<Company>(
      this.query()
        .filter('aircraft_ids', 'cs', `{${aircraftId}}`)
        .eq('is_active', true)
    )
  }

  /**
   * Get companies with multiple aircraft
   */
  static async getFleetOperators(minAircraft = 2): Promise<Company[]> {
    const result = await intelliJetNetService
      .rpc('get_companies_with_min_aircraft', { min_aircraft: minAircraft })
    return ensureArray<Company>(result)
  }

  /**
   * Get all unique business types
   */
  static async getBusinessTypes(): Promise<string[]> {
    const data = await executeQuery<{ business_type: string }>(
      intelliJetNetService
        .from(this.TABLE_NAME)
        .select('business_type')
        .eq('is_active', true)
    )

    const uniqueTypes = [...new Set(data
      .map(item => item.business_type)
      .filter(Boolean))]
    
    return uniqueTypes.sort()
  }

  /**
   * Get companies with pagination
   */
  static async getPaginated(params: {
    page?: number
    limit?: number
    sortBy?: 'company_name' | 'created_at' | 'updated_at'
    sortOrder?: 'asc' | 'desc'
    businessType?: string
    country?: string
  } = {}): Promise<{ data: Company[]; total: number }> {
    const page = params.page || 1
    const limit = params.limit || 50
    const offset = (page - 1) * limit
    const sortBy = params.sortBy || 'company_name'
    const sortOrder = params.sortOrder || 'asc'

    let query = this.query()
      .eq('is_active', true)
      .order(sortBy, sortOrder === 'asc')
      .limit(limit)
      .offset(offset)
      .count('exact')

    if (params.businessType) {
      query = query.eq('business_type', params.businessType)
    }
    if (params.country) {
      query = query.eq('country', params.country)
    }

    const result = await query.execute<Company>()
    return {
      data: ensureArray<Company>(result.data),
      total: result.count || 0
    }
  }

  /**
   * Get company statistics
   */
  static async getStatistics(): Promise<{
    total: number
    byCountry: Record<string, number>
    byBusinessType: Record<string, number>
    withWebsite: number
  }> {
    const { count: total } = await this.query()
      .eq('is_active', true)
      .count('exact')
      .execute<Company>()

    const { count: withWebsite } = await this.query()
      .eq('is_active', true)
      .neq('website', null)
      .count('exact')
      .execute<Company>()

    // Get by country
    const countryResult = await intelliJetNetService
      .rpc('get_companies_by_country_count')
    const countryData = ensureArray<{ country: string; count: number }>(countryResult)

    // Get by business type
    const typeResult = await intelliJetNetService
      .rpc('get_companies_by_type_count')
    const typeData = ensureArray<{ business_type: string; count: number }>(typeResult)

    return {
      total: total || 0,
      withWebsite: withWebsite || 0,
      byCountry: countryData.reduce((acc: Record<string, number>, item) => {
        acc[item.country] = item.count
        return acc
      }, {}),
      byBusinessType: typeData.reduce((acc: Record<string, number>, item) => {
        acc[item.business_type] = item.count
        return acc
      }, {})
    }
  }

  /**
   * Get recently updated companies
   */
  static async getRecentlyUpdated(days = 7): Promise<Company[]> {
    const date = new Date()
    date.setDate(date.getDate() - days)

    return executeQuery<Company>(
      this.query()
        .gte('updated_at', date.toISOString())
        .eq('is_active', true)
        .order('updated_at', false)
        .limit(100)
    )
  }
}

export default CompaniesService